import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useRouteMatch } from "react-router-dom";
import styled from "styled-components";
import {
	ActionButton,
	Button,
	CheckboxToggle,
	Form as BaseForm,
	FormContainer as BaseFormContainer,
	Input,
	InputValidation,
	Label,
	RequiredMarker,
	ScrollToTop,
	Select,
} from "@components";
import Routes, { CustomerRouteParams } from "@service/navigation/routes";
import { QueryState, useStaticData } from "@hooks";
import Customer, {
	CustomerStatus,
	NewCustomer,
	UpdatedCustomer,
} from "@models/Customer";
import {
	checkPricingSettings,
	initialPricingSettings,
} from "../../products/components/util";
import PricingSettingsList from "../../division/components/SettingsPage/PricingSettingsList";
import PricingSettingsPageContext from "../../division/components/SettingsPage/PricingSettingsPageContext";
import { REQUIRED_ERROR_MESSAGE } from "@util/errors";
import { OneOf } from "@util/types";

type Props<T extends NewCustomer | UpdatedCustomer> = {
	customer?: Customer;
	handleOnSubmit: (customer: T) => void;
	cmdState: QueryState<Customer>;
};

export default function CustomerForm<T extends NewCustomer | UpdatedCustomer>(
	props: Props<T>
) {
	const { customer, handleOnSubmit, cmdState } = props;
	const {
		params: { customerId },
	} = useRouteMatch<CustomerRouteParams>();

	const { legalForms, products } = useStaticData();

	const isEditMode = customerId !== undefined;

	const methods = useForm<OneOf<NewCustomer, UpdatedCustomer>>({
		defaultValues: {
			...(customer ?? {
				name: "",
				status: CustomerStatus.Trial,
				legalForm: legalForms[0].description,
			}),
			...(isEditMode
				? {
						pricingSettings: initialPricingSettings(
							customer?.pricingSettings ?? [],
							products
						),
				  }
				: {}),
		},
		mode: "onChange",
		shouldUnregister: false,
	});

	const { formState, register, handleSubmit, setValue, watch } = methods;

	const isTrialCustomer = watch("status") === CustomerStatus.Trial;
	const toggleStatus = () => {
		setValue(
			"status",
			isTrialCustomer ? CustomerStatus.Active : CustomerStatus.Trial
		);
	};

	const onSubmit = handleSubmit((values) => {
		return handleOnSubmit({
			...values,
			...("pricingSettings" in values
				? {
						pricingSettings: values.pricingSettings
							? checkPricingSettings(values.pricingSettings)
							: null,
				  }
				: {}),
		} as T);
	});

	return (
		<PricingSettingsPageContext.Provider
			value={{ pricingSettings: customer?.pricingSettings ?? null }}
		>
			<FormProvider {...methods}>
				<ScrollToTop />
				<FormContainer>
					{isEditMode ? (
						<Heading>Kunden bearbeiten</Heading>
					) : (
						<Heading>Neuer Kunde</Heading>
					)}
					<Form onSubmit={onSubmit}>
						<BaseDataSection>
							<InputValidation error={formState.errors.name}>
								<InputContainer className="form-group">
									<Label htmlFor="name">
										Name <RequiredMarker />
									</Label>
									<Input
										{...register("name", { required: REQUIRED_ERROR_MESSAGE })}
										type="text"
										id="name"
									/>
								</InputContainer>
							</InputValidation>
							<InputContainer className="form-group">
								<Label htmlFor="legalForm">
									Rechtsform <RequiredMarker />
								</Label>
								<Select
									{...register("legalForm", { required: true })}
									id="legalForm"
								>
									{legalForms.map((l) => (
										<option key={l.legalFormId} value={l.description}>
											{l.description}
										</option>
									))}
								</Select>
							</InputContainer>
						</BaseDataSection>
						{!isEditMode && (
							<InputContainer className="form-group">
								<CheckboxToggle
									label="Ist Trialkunde"
									data-testid="customer-form-isTrialCustomer"
									onChange={toggleStatus}
									disabled={isEditMode}
									checked={isTrialCustomer}
								/>
							</InputContainer>
						)}
						{isEditMode && <PricingSettingsList />}
						<InputContainer>
							<ActionButton
								type="submit"
								query={cmdState}
								successContent={"Gespeichert"}
							>
								Speichern
							</ActionButton>
							<Link to={Routes.Customers.makePath()}>
								<Button type="button" data-testid="loginButton">
									Zurück
								</Button>
							</Link>
						</InputContainer>
					</Form>
				</FormContainer>
			</FormProvider>
		</PricingSettingsPageContext.Provider>
	);
}

const InputContainer = styled.div``;

const Heading = styled.h3`
	text-align: center;
`;

const Form = styled(BaseForm)`
	gap: 30px;
`;

const FormContainer = styled(BaseFormContainer)`
	width: 750px;
`;

const BaseDataSection = styled.section`
	display: flex;
	flex-direction: column;
`;
